import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { InputComponent } from '../../../shared/input/input.component';
import { ButtonsComponent } from '../../../shared/buttons/buttons.component';
import { CommonModule } from '@angular/common';
import * as PopupConstants from '../../../shared/constants/popup-constants';
import { Analysis } from '../../../interfaces/analysis';
import { ShareItemsService } from '../../../services/share-feature/share-items.service';
import { forkJoin, lastValueFrom } from 'rxjs';
import { SearchableMultiSelectComponent } from '../../../shared/searchable-multi-select/searchable-multi-select.component';
import { noWhitespaceValidator } from '../../../shared/validators/no-whitespace.validator';

@Component({
  selector: 'app-analysis-edit-modal',
  standalone: true,
  imports: [
    InputComponent,
    ButtonsComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    CommonModule,
    SearchableMultiSelectComponent,
  ],
  templateUrl: './analysis-edit-modal.component.html',
  styleUrl: './analysis-edit-modal.component.css',
})
export class AnalysisEditModalComponent implements OnInit {
  editForm = new FormGroup({
    Name: new FormControl('', [Validators.required, Validators.maxLength(256), noWhitespaceValidator()]),
    Description: new FormControl('', [Validators.maxLength(1024), noWhitespaceValidator()]),
    groups: new FormControl<string[]>([]),
  });
  fullGroupsList: { GroupID: number; GroupName: string; IsPrimary: boolean }[] = [];
  groupOptions: string[] = [];
  initialSelectedGroups: string[] = []; // Renamed for clarity
  disabledGroupOptions: string[] = [];
  primaryGroup: any = null;
  isSubmitting = false;
  buttonCancel = PopupConstants.BUTTON_CANCEL;
  arialLabel = PopupConstants.ARIAL_LABEL;
  btnMat = PopupConstants.BTN_MAT;
  btnStyleText = PopupConstants.BTN_STYLE_TEXT;
  buttonSave = PopupConstants.BUTTON_SAVE;
  btnMatFlat = PopupConstants.BTN_MAT_FLAT;
  btnStyleFilledSmall = PopupConstants.BTN_STYLE_FILLED_SMALL;
  btnStyleFilledSmallDisabled = PopupConstants.BTN_STYLE_FILLED_SMALL_DISABLED;

  constructor(
    public dialogRef: MatDialogRef<AnalysisEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { analysisData: Analysis },
    private shareItemsService: ShareItemsService
  ) {
    // Pre-fill the form with the existing analysis data
    if (data && data.analysisData) {
      this.editForm.get('Name')?.setValue(data.analysisData.AnalysisName);
      this.editForm.get('Description')?.setValue(data.analysisData.AnalysisDescription);
    }
  }
  ngOnInit(): void {
    this.initializeGroupControl();
  }

  initializeGroupControl() {
    this.fullGroupsList = this.data.analysisData.Groups || [];

    this.primaryGroup = this.fullGroupsList.find((g: any) => g.IsPrimary);

    this.groupOptions = this.fullGroupsList.map((g) => g.GroupName);

    this.initialSelectedGroups = this.fullGroupsList.map((g) => g.GroupName);
    this.editForm.get('groups')?.setValue(this.initialSelectedGroups);
    if (this.primaryGroup) {
      this.disabledGroupOptions = [this.primaryGroup.GroupName];
    }
  }
  closePopup(): void {
    this.dialogRef.close({ event: 'CANCEL' });
  }

  async onEditSubmit(): Promise<void> {
    if (this.editForm.valid && this.editForm.dirty) {
      this.isSubmitting = true;
      const currentSelection = this.editForm.get('groups')?.value || [];
      const removedGroupNames = this.initialSelectedGroups.filter((name: string) => !currentSelection.includes(name));

      const apiCalls: any = [];
      const analysisId = this.data.analysisData.AnalysisID || this.data.analysisData.AnalysisID; // Handle both casing if needed

      if (removedGroupNames.length > 0) {
        const removedGroupIds = this.fullGroupsList
          .filter((g) => removedGroupNames.includes(g.GroupName))
          .map((g) => g.GroupID);

        removedGroupIds.forEach((groupId) => {
          apiCalls.push(this.shareItemsService.removeResourceFromGroup('analyses', analysisId, groupId));
        });
      }

      if (apiCalls.length > 0) {
        try {
          await lastValueFrom(forkJoin(apiCalls));
        } catch (error) {
          console.error('Error removing groups', error);
        }
      }
      const updatedFields: any = {};
      const nameControl = this.editForm.get('Name');
      const descControl = this.editForm.get('Description');

      if (nameControl?.dirty) updatedFields['Name'] = nameControl.value;
      if (descControl?.dirty) updatedFields['Description'] = descControl.value;

      const hasMetadataChanges = Object.keys(updatedFields).length > 0;
      const hasGroupChanges = removedGroupNames.length > 0;

      if (hasMetadataChanges) {
        this.dialogRef.close({
          data: updatedFields,
          analysisId: this.data.analysisData.AnalysisID,
          event: 'SUBMIT',
        });
      } else if (hasGroupChanges) {
        this.dialogRef.close({
          event: 'RELOAD',
        });
      } else {
        this.closePopup();
      }
    }
  }
}
